import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { Button } from '@/design-system/button';
import { DeleteIcon, EditIcon } from '@/icons';
import { ToggleChargeDetailStatusPayload } from '@/services/chargeSlab/chargeSlab.mutation';
import { ChargeSlab } from '@/services/chargeSlab/chargeSlab.types';
import { ColumnDef } from '@tanstack/react-table';
import { BanIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { ManageChargeSlabs } from '../ManageChargeSlabs';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

type OpenChargesCategoryProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
}) => void;

export function getColumns(
  onOpen: OnOpenProps,
  t: (key: string) => string,
  openConfirmation: OpenChargesCategoryProps,
  mutate: (payload: ToggleChargeDetailStatusPayload) => Promise<unknown>,
  categoryId?: string,
  slabs?: string,
): ColumnDef<ChargeSlab>[] {
  return [
    {
      accessorKey: 'chargeCategoryDetailId',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'fromAmount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.fromAmount')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('fromAmount')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'toAmount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.toAmount')} />,
      cell: ({ row }) => <div>{row.getValue('toAmount')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'chargeType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.chargeType')} />,
      cell: ({ row }) => {
        return row.getValue('chargeType') === 'F' ? (
          <div className="font-medium truncate max-w-[20rem] text-info">
            {t('configurationManagement.commissions.table.title.flat')}
          </div>
        ) : (
          <div className="font-medium truncate max-w-[20rem] text-info">
            {t('configurationManagement.commissions.table.title.percentage')}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: 'chargeValue',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.value')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('chargeValue')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'minChargeValue',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.minValue')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('minChargeValue')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'maxChargeValue',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.maxValue')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('maxChargeValue')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === 'A' ? (
              <div className="text-success">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive">{t('common.page.table.suspended')}</div>
            )}
          </div>
        );
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <EditIcon
            className="text-foreground-subtle size-4 cursor-pointer"
            onClick={() =>
              onOpen({
                title: 'Manage Commission',
                okText: <CrumbTitle id="configurationManagement.charges.manageChargeSlabModal.submit" />,
                size: 'lg',
                modalTitle: 'configurationManagement.charges.manageChargeSlabModal.manageSlabs',
                component: (
                  <ManageChargeSlabs
                    categoryId={categoryId}
                    categoryName={slabs}
                    chargeCategoryDetailId={row.original.chargeCategoryDetailId}
                  />
                ),
              })
            }
          />
          <Button
            variant={'ghost'}
            className="text-foreground-subtle size-4 p-0 cursor-pointer"
            onClick={() => {
              const isActive = row.getValue('status') === 'A';
              openConfirmation({
                title: t('common.page.modal.confirmation.title'),
                description: isActive
                  ? t('configurationManagement.charges.suspendChargeCategorySlabModal.content')
                  : t('configurationManagement.charges.unsuspendChargeCategorySlabModal.content'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                size: 'lg',
                onAction: async () => {
                  await mutate({
                    chargeCategoryDetailId: row.getValue('chargeCategoryDetailId'),
                    status: row.getValue('status') === 'A' ? 'S' : 'A',
                  });
                },
                variant: 'warning',
              });
            }}
          >
            {row.getValue('status') === 'A' ? (
              <BanIcon className="text-status-neutral size-4 " />
            ) : (
              <BanIcon className="text-warning size-4 " />
            )}
          </Button>
          <DeleteIcon
            className="text-destructive size-4 cursor-pointer"
            onClick={() => {
              openConfirmation({
                title: t('common.page.modal.confirmation.title'),
                description: t('configurationManagement.charges.deleteChargeCategorySlabModal.content'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                size: 'lg',
                onAction: async () => {
                  await mutate({
                    chargeCategoryDetailId: row.getValue('chargeCategoryDetailId'),
                    status: 'D',
                  });
                },
                variant: 'destructive',
              });
            }}
          />
        </div>
      ),
      size: 40,
    },
  ];
}
